import type { GridApi, IRowNode } from "ag-grid-community";
import React, { useEffect, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";

import { ButtonStandard, Text } from "@performyard/py-components";

import { SurveyResultsBarChart } from "./SurveyResultsBarChart";

import { SurveyQuestionResult } from "../types";

import { sortBarChartOptions } from "./barChartUtils";

type SurveyResultsSuperRowProps = {
	data: SurveyQuestionResult;
	api: GridApi;
	node: IRowNode;
};

export const SurveyResultsSuperRow: React.FC<SurveyResultsSuperRowProps> = ({ data, api, node }) => {
	const [showAll, setShowAll] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null!);

	const allOptions = data.options_results ?? [];
	const hasZeroCountOptions = allOptions.some((option) => option.response_count === 0);

	const optionsWithResponses = allOptions.filter((option) => option.response_count > 0);
	const visibleOptions = showAll ? sortBarChartOptions(allOptions) : sortBarChartOptions(optionsWithResponses);

	const { height = 0 } = useResizeObserver<HTMLDivElement>({ ref: containerRef, box: "border-box" });

	useEffect(() => {
		if (height > 0 && node.rowHeight !== height) {
			node.setRowHeight(height);
			api.onRowHeightChanged();
		}
	}, [height, api, node]);

	return (
		<div ref={containerRef} className="px-3 py-4">
			<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="mb-4">
				{data.question_text}
			</Text>
			<SurveyResultsBarChart
				options={visibleOptions}
				totalResponses={data.response_count}
				questionType={data.question_type}
			/>
			<div className="mt-3 flex items-center gap-2 text-sm">
				{hasZeroCountOptions && (
					<div>
						<ButtonStandard
							variant="text"
							size="small"
							color="primary"
							dangerouslySetClassName="h-auto font-normal"
							onClick={() => setShowAll((prev) => !prev)}
						>
							{showAll ? "Hide All Options" : "View All Options"}
						</ButtonStandard>
					</div>
				)}
			</div>
		</div>
	);
};
